/**
 * Treat user table edits as observed anchors for Buildings electricity / gas Customize.
 *
 * - User-edited years (and original observed years) are anchors.
 * - Between consecutive anchors: linear interpolation.
 * - Years before the first anchor: scale by (newFirst / oldFirst) so YoY ratios in the
 *   pre-anchor segment (including first-anchor vs prior year) match the pre-edit shape.
 * - Years after the last anchor: keep original exterior values.
 */

export type EnergySeriesProvenance = 'observed' | 'interpolated' | 'extrapolated';

export function provenanceByYearFromChartDatasets(
  labels: number[],
  datasets: any[] | undefined | null
): Map<number, EnergySeriesProvenance> {
  const map = new Map<number, EnergySeriesProvenance>();
  const ds = datasets || [];
  labels.forEach((year, i) => {
    for (const d of ds) {
      const v = Number(d?.data?.[i]);
      if (!Number.isFinite(v) || v <= 0) continue;
      const label = String(d?.label || '').toLowerCase();
      if (label.includes('observed')) {
        map.set(year, 'observed');
      } else if (label.includes('interpolat')) {
        map.set(year, 'interpolated');
      } else if (label.includes('extrapola')) {
        map.set(year, 'extrapolated');
      } else {
        map.set(year, 'observed');
      }
      break;
    }
    if (!map.has(year)) map.set(year, 'extrapolated');
  });
  return map;
}

/**
 * Rebuild a sector series after Customize edits.
 */
export function applyUserObservedAnchors(params: {
  years: number[];
  /** Values from the table edit payload (may still contain stale non-anchor years). */
  editedValuesByYear: Map<number, number>;
  originalValuesByYear: Map<number, number>;
  originalProvenanceByYear: Map<number, EnergySeriesProvenance>;
  /** Years the user explicitly edited for THIS sector. */
  modifiedYears: Set<number>;
}): { values: number[]; sources: EnergySeriesProvenance[] } {
  const {
    years,
    editedValuesByYear,
    originalValuesByYear,
    originalProvenanceByYear,
    modifiedYears,
  } = params;

  const anchors = new Set<number>();
  for (const y of years) {
    if (originalProvenanceByYear.get(y) === 'observed' || modifiedYears.has(y)) {
      anchors.add(y);
    }
  }
  const anchorYears = Array.from(anchors).sort((a, b) => a - b);

  const valueAtAnchor = (y: number): number => {
    if (modifiedYears.has(y)) {
      const edited = editedValuesByYear.get(y);
      if (edited != null && Number.isFinite(edited)) return edited;
    }
    const orig = originalValuesByYear.get(y);
    return orig != null && Number.isFinite(orig) ? orig : 0;
  };

  const values: number[] = new Array(years.length).fill(0);
  const sources: EnergySeriesProvenance[] = new Array(years.length).fill('extrapolated');

  if (anchorYears.length === 0) {
    years.forEach((y, i) => {
      values[i] = Math.round(originalValuesByYear.get(y) ?? 0);
      sources[i] = originalProvenanceByYear.get(y) ?? 'extrapolated';
    });
    return { values, sources };
  }

  const yFirst = anchorYears[0];
  const yLast = anchorYears[anchorYears.length - 1];
  const oldFirst = originalValuesByYear.get(yFirst) ?? 0;
  const newFirst = valueAtAnchor(yFirst);
  const priorScale =
    oldFirst > 0 && Number.isFinite(newFirst) && Math.abs(newFirst - oldFirst) > 0.5
      ? newFirst / oldFirst
      : 1;

  years.forEach((y, i) => {
    if (anchors.has(y)) {
      values[i] = Math.round(valueAtAnchor(y));
      sources[i] = 'observed';
      return;
    }

    // Before first anchor: preserve historical YoY shape via ratio scale to new first-anchor value
    if (y < yFirst) {
      const orig = originalValuesByYear.get(y) ?? 0;
      values[i] = Math.round(orig * priorScale);
      sources[i] = originalProvenanceByYear.get(y) ?? 'extrapolated';
      return;
    }

    // After last anchor: keep original exterior
    if (y > yLast) {
      values[i] = Math.round(originalValuesByYear.get(y) ?? 0);
      sources[i] = originalProvenanceByYear.get(y) ?? 'extrapolated';
      return;
    }

    // Interior between anchors: linear interpolation
    let y0 = -1;
    let y1 = -1;
    for (let a = 0; a < anchorYears.length - 1; a++) {
      if (anchorYears[a] < y && y < anchorYears[a + 1]) {
        y0 = anchorYears[a];
        y1 = anchorYears[a + 1];
        break;
      }
    }
    if (y0 >= 0 && y1 >= 0) {
      const v0 = valueAtAnchor(y0);
      const v1 = valueAtAnchor(y1);
      const t = (y - y0) / (y1 - y0);
      values[i] = Math.round(v0 + t * (v1 - v0));
      sources[i] = 'interpolated';
      return;
    }

    values[i] = Math.round(originalValuesByYear.get(y) ?? 0);
    sources[i] = originalProvenanceByYear.get(y) ?? 'extrapolated';
  });

  return { values, sources };
}

export const ENERGY_SECTOR_COLORS = {
  residential: {
    observed: '#1e40af',
    interpolated: '#3b82f6',
    extrapolated: '#8b5cf6',
    userModified: '#ef4444',
  },
  commercial: {
    observed: '#166534',
    interpolated: '#22c55e',
    extrapolated: '#f59e0b',
    userModified: '#ef4444',
  },
} as const;
